import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { DATE_FORMATS, SEND_NOW } from 'common/constants/SharedConstants';
import CustomDropdown from 'molecules/CustomDropdown/Container/CustomDropdown';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';

const GiftCardDeliveryDate = props => {
  const { fieldConfig, formConfig, onFormFieldChangeHandler } = props;
  const [selectedValue, setSelectedValue] = useState(fieldConfig?.defaultValue);
  const [showScheduleDateTime, setShowScheduleDateTime] = useState(false);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const maxDate = new Date(tomorrow);
  maxDate.setDate(maxDate.getDate() + 30);
  const [selectedDate, setSelectedDate] = useState(tomorrow);
  const [selectedTime, setSelectedTime] = useState(null);
  const { DATE, TIME, SELECT_TIME } = TRANSLATE_MAPPING_KEY;
  const { SingleArrowBlackExpand, SingleArrowBlack } = IMAGE_PATH;
  const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);

  const datePickerRef = useRef(null);
  const parseData = fieldConfig => {
    const { values = [] } = fieldConfig ?? {};

    return values.reduce((acc, item) => {
      try {
        const parsed = JSON.parse(item);
        if (parsed) acc.push(parsed);
      } catch (error) {
        console.log('Error parsing item');
      }
      return acc;
    }, []);
  };

  const parsedData = parseData(fieldConfig);

  useEffect(() => {
    if (selectedDate) {
      const details = formConfig?.giftCardDetails?.recipientDetails;
      if (details && formConfig?.name >= 0) {
        details[formConfig?.name].selectedDate = selectedDate;
        formConfig?.updateGiftCardDetails({ recipientDetails: details });
      }
    }
  }, [selectedDate]);

  const handleRadioChange = event => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    if (newValue === SEND_NOW) {
      setShowScheduleDateTime(false);
      setSelectedDate(new Date());
      setSelectedTime(null);
      const details = formConfig?.giftCardDetails?.recipientDetails;
      if (details) {
        details[formConfig?.name].selectedTime = null;
        formConfig?.updateGiftCardDetails({ recipientDetails: details });
      }
    } else {
      setShowScheduleDateTime(true);
    }
    onFormFieldChangeHandler(event, formConfig, fieldConfig);
  };

  const handleDateChange = date => {
    setSelectedDate(date);
    setDatePickerIsOpen(false);
  };

  const formatTime = hour => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    if (hour === 0) {
      return `0:00 ${DATE_FORMATS.AM}`;
    }
    const options = { hour: 'numeric', minute: 'numeric', hour12: true };
    return date.toLocaleString([], options);
  };

  const generateTimeSlots = () => {
    const slots = [];

    let startHour = 0;
    for (let i = startHour; i < 24; i++) {
      const formattedTime = formatTime(i);
      slots.push({
        key: formattedTime,
        value: formattedTime,
      });
    }

    return slots;
  };

  const handleTimeSelect = item => {
    const details = formConfig?.giftCardDetails?.recipientDetails;
    if (!details) return;
    details[formConfig?.name].selectedTime = item.value;
    formConfig?.updateGiftCardDetails({ recipientDetails: details });

    setSelectedTime(item);
  };

  const handleDatePickerClick = event => {
    if (!event.target?.closest('.react-datepicker')) {
      setDatePickerIsOpen(prev => !prev);
    }
  };
  useEffect(() => {
    if (fieldConfig?.defaultValue && fieldConfig?.value === undefined && fieldConfig) {
      const event = {
        target: {
          value: fieldConfig.defaultValue,
        },
      };
      onFormFieldChangeHandler(event, formConfig, fieldConfig);
    }
  }, [fieldConfig?.defaultValue, fieldConfig]);

  const handleClickOutside = event => {
    const isInsideDatePicker = datePickerRef?.current && datePickerRef?.current?.contains(event.target);
    const isNavigationArrow = event?.target?.classList?.contains('react-datepicker__navigation');

    if (!isInsideDatePicker && !isNavigationArrow) {
      setDatePickerIsOpen(false);
    }
  };

  useEffect(() => {
    if (datePickerIsOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [datePickerIsOpen]);

  return (
    <div className='giftCardDeliveryDataTimeSection'>
      {fieldConfig?.label && (
        <div className='form-radio-label'>
          <span>{fieldConfig?.label}</span>
        </div>
      )}
      {parsedData?.map((option, index) => {
        const label = Object.values(option)[0];
        const value = Object.keys(option)[0];
        return (
          <div key={index} className='form-radio-option'>
            <input
              type='radio'
              id={`${fieldConfig?.name}-m${formConfig?.name}-radio-${index}`}
              name={`${fieldConfig?.name}-m${formConfig?.name}-radio-${index}`}
              data-testid={`radio-${value}`}
              value={value}
              checked={selectedValue === value}
              onChange={handleRadioChange}
            />
            <label htmlFor={`${fieldConfig?.name}-m${formConfig?.name}-radio-${index}`} title={value}>
              {label}
            </label>
          </div>
        );
      })}

      {showScheduleDateTime && (
        <div className='date-time-section date-time-section-au'>
          <div className='date-picker-section'>
            <label className='date-title'>{translateWithI18Next(DATE)}</label>
            <div className='date-value' onClick={handleDatePickerClick} ref={datePickerRef}>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={tomorrow}
                maxDate={maxDate}
                dateFormat={DATE_FORMATS.MMMM_d_YYYY}
                open={datePickerIsOpen}
                readOnly={true}
              />
              <img
                src={datePickerIsOpen ? SingleArrowBlackExpand : SingleArrowBlack}
                alt=' '
                className='downarrow-imgs'
              />
            </div>
          </div>
          <CustomDropdown
            cssClass={`date-time-dropdown`}
            dropdownTitle={translateWithI18Next(TIME)}
            dateTimeArr={[]}
            selectedItem={selectedTime}
            options={generateTimeSlots()}
            dropdownId={'time-id'}
            placeholderText={translateWithI18Next(SELECT_TIME)}
            setSelectedItem={handleTimeSelect}
          />
        </div>
      )}
    </div>
  );
};

export default GiftCardDeliveryDate;
